import React, { useState } from 'react';
import { Box, Grid, Paper, Typography, Button, Select, MenuItem, FormControl, InputLabel, Stack, Container } from '@mui/material';
import { ChromePicker, ColorResult } from 'react-color';
import { ContentCopy, Save, Code, DataObject, Favorite } from '@mui/icons-material';

interface PaletteColor {
  name: string;
  hex: string;
}

function hexToRgb(hex: string) {
  const match = hex.replace('#', '').match(/.{1,2}/g);
  if (!match) return [0, 0, 0];
  return match.map(x => parseInt(x, 16));
}

function rgbToHex(r: number, g: number, b: number) {
  return (
    '#' +
    [r, g, b]
      .map(x => x.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function hslToRgb(h: number, s: number, l: number) {
  h = h / 360;
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

const paletteTypes = [
  { value: 'monochromatic', label: 'Monokromatik' },
  { value: 'analogous', label: 'Analog' },
  { value: 'complementary', label: 'Tamamlayıcı' },
  { value: 'triadic', label: 'Üçlü' },
  { value: 'split-complementary', label: 'Dik' },
];

function generatePalette(baseHex: string, type: string): PaletteColor[] {
  const rgb = hexToRgb(baseHex);
  const hsl = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const h = hsl[0], s = hsl[1], l = hsl[2];
  const palette: PaletteColor[] = [];
  if (type === 'monochromatic') {
    palette.push({ name: 'Ana Renk', hex: baseHex });
    for (let i = 1; i <= 2; i++) {
      const newL = Math.min(1, l + 0.2 * i);
      const rgbArr = hslToRgb(h, s, newL);
      palette.push({ name: `Açık Ton ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
    for (let i = 1; i <= 2; i++) {
      const newL = Math.max(0, l - 0.2 * i);
      const rgbArr = hslToRgb(h, s, newL);
      palette.push({ name: `Koyu Ton ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
  } else if (type === 'analogous') {
    palette.push({ name: 'Ana Renk', hex: baseHex });
    for (let i = 1; i <= 2; i++) {
      let newH = (h + 30 * i) % 360;
      let rgbArr = hslToRgb(newH, s, l);
      palette.push({ name: `Analog +${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
      newH = (h - 30 * i + 360) % 360;
      rgbArr = hslToRgb(newH, s, l);
      palette.push({ name: `Analog -${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
  } else if (type === 'complementary') {
    palette.push({ name: 'Ana Renk', hex: baseHex });
    const compH = (h + 180) % 360;
    let rgbArr = hslToRgb(compH, s, l);
    palette.push({ name: 'Tamamlayıcı', hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    for (let i = 1; i <= 2; i++) {
      const newL = Math.min(1, l + 0.2 * i);
      rgbArr = hslToRgb(h, s, newL);
      palette.push({ name: `Açık Ton ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
      rgbArr = hslToRgb(compH, s, newL);
      palette.push({ name: `Tamamlayıcı Açık ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
  } else if (type === 'triadic') {
    palette.push({ name: 'Ana Renk', hex: baseHex });
    for (let i = 1; i <= 2; i++) {
      const newH = (h + 120 * i) % 360;
      const rgbArr = hslToRgb(newH, s, l);
      palette.push({ name: `Üçlü Renk ${i + 1}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
    for (let i = 1; i <= 2; i++) {
      const newL = Math.min(1, l + 0.2 * i);
      const rgbArr = hslToRgb(h, s, newL);
      palette.push({ name: `Açık Ton ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
  } else if (type === 'split-complementary') {
    palette.push({ name: 'Ana Renk', hex: baseHex });
    const compH = (h + 180) % 360;
    for (let i = -1; i <= 1; i += 2) {
      const newH = (compH + 30 * i + 360) % 360;
      const rgbArr = hslToRgb(newH, s, l);
      palette.push({ name: `Dik Tamamlayıcı ${i > 0 ? 'Sağ' : 'Sol'}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
    for (let i = 1; i <= 2; i++) {
      const newL = Math.min(1, l + 0.2 * i);
      const rgbArr = hslToRgb(h, s, newL);
      palette.push({ name: `Açık Ton ${i}`, hex: rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]) });
    }
  }
  return palette;
}

function getPaletteInfo(type: string, palette: PaletteColor[], baseHex: string) {
  return `Palet Türü: ${paletteTypes.find(t => t.value === type)?.label}\nRenk Sayısı: ${palette.length}\nAna Renk: ${baseHex}`;
}

function getUsageSuggestions(type: string) {
  switch (type) {
    case 'monochromatic':
      return 'Monokromatik paletler tek bir rengin farklı tonlarını içerir. Profesyonel ve şık tasarımlar için idealdir. Web siteleri, kurumsal kimlikler ve minimalist tasarımlar için uygundur.';
    case 'analogous':
      return 'Analog paletler renk çemberinde birbirine yakın renkleri içerir. Doğal ve uyumlu görünüm sağlar. Doğa temalı tasarımlar için idealdir.';
    case 'complementary':
      return 'Tamamlayıcı paletler renk çemberinde karşılıklı duran renkleri içerir. Yüksek kontrast ve canlılık sağlar. Dikkat çekici başlıklar, vurgu öğeleri için uygundur.';
    case 'triadic':
      return 'Üçlü paletler renk çemberinde eşit aralıklarla yer alan üç rengi içerir. Dengeli ve dinamik bir görünüm sağlar. Modern web tasarımları, logo tasarımları için idealdir.';
    case 'split-complementary':
      return 'Dik paletler ana rengin tamamlayıcısının yanındaki renkleri içerir. Yüksek kontrast ile yumuşak bir görünüm sağlar. Yaratıcı tasarımlar, sanatsal projeler için uygundur.';
    default:
      return '';
  }
}

const FAVORITES_KEY = 'favorites_v1';

const ColorPalette: React.FC = () => {
  const [baseColor, setBaseColor] = useState('#2196f3');
  const [paletteType, setPaletteType] = useState('monochromatic');
  const [palette, setPalette] = useState<PaletteColor[]>(generatePalette('#2196f3', 'monochromatic'));
  const [copied, setCopied] = useState<string | null>(null);
  const [favAdded, setFavAdded] = useState(false);

  const handleBaseColorChange = (color: ColorResult) => {
    setBaseColor(color.hex);
    setPalette(generatePalette(color.hex, paletteType));
  };

  const handlePaletteTypeChange = (event: any) => {
    const newType = event.target.value;
    setPaletteType(newType);
    setPalette(generatePalette(baseColor, newType));
  };

  const handleCopy = (text: string, which: string) => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleAddFavorite = () => {
    const fav = {
      id: 'palette-' + baseColor + '-' + paletteType,
      type: 'palette',
      name: `Palet (${paletteTypes.find(t => t.value === paletteType)?.label})`,
      data: { baseColor, paletteType, colors: palette },
      date: new Date().toISOString(),
    };
    let favs: any[] = [];
    try {
      favs = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    } catch { }
    if (!favs.some((f: any) => f.id === fav.id)) {
      favs.unshift(fav);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
      window.dispatchEvent(new Event('favoriteAdded'));
      setFavAdded(true);
      setTimeout(() => setFavAdded(false), 1500);
    }
  };

  const getPaletteCSS = () => {
    return [
      `/* Renk Paleti - ${paletteTypes.find(t => t.value === paletteType)?.label} */`,
      ':root {',
      ...palette.map((color: PaletteColor) => `    --${color.name.toLowerCase().replace(/ /g, '-')}: ${color.hex};`),
      '}'
    ].join('\n');
  };

  const getPaletteJSON = () => {
    return JSON.stringify({
      paletteType: paletteTypes.find(t => t.value === paletteType)?.label,
      colors: palette
    }, null, 2);
  };

  // URL ile paylaşma
  const getShareURL = () => {
    const colors = palette.map((c: PaletteColor) => c.hex.replace('#', '')).join('-');
    return `${window.location.origin}${window.location.pathname}?type=${paletteType}&colors=${colors}`;
  };

  const handleShare = () => {
    const url = getShareURL();
    navigator.clipboard.writeText(url);
    handleCopy(url, 'url');
  };

  // ASE formatı (basitleştirilmiş)
  const getASEContent = () => {
    return `GIMP Palette
Name: Color Palette ${paletteTypes.find(t => t.value === paletteType)?.label}
#
${palette.map((c: PaletteColor) => {
      const r = parseInt(c.hex.slice(1, 3), 16);
      const g = parseInt(c.hex.slice(3, 5), 16);
      const b = parseInt(c.hex.slice(5, 7), 16);
      return `${r} ${g} ${b} ${c.name}`;
    }).join('\n')}`;
  };

  const downloadASE = () => {
    const content = getASEContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'palette.gpl';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadJSON = () => {
    const content = getPaletteJSON();
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'palette.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>Renk Paleti Oluşturucu</Typography>
        <Button
          variant="contained"
          color={favAdded ? 'success' : 'secondary'}
          startIcon={<Favorite />}
          onClick={handleAddFavorite}
          sx={{ mb: 2 }}
          disabled={favAdded}
        >
          {favAdded ? 'Favorilere Eklendi' : 'Favorilere Ekle'}
        </Button>
        {/* Üst: Ana renk ve palet türü seçici */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Ana Renk Seçin</Typography>
              <ChromePicker color={baseColor} onChange={handleBaseColorChange} />
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Palet Türü</InputLabel>
                <Select value={paletteType} label="Palet Türü" onChange={handlePaletteTypeChange}>
                  {paletteTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 1, minHeight: 320 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Palet Önizleme</Typography>
                  <Stack spacing={1}>
                    {palette.map((color, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ width: 40, height: 40, backgroundColor: color.hex, borderRadius: 1, mr: 2, boxShadow: 2 }} />
                        <Box>
                          <Typography fontWeight={700}>{color.name}</Typography>
                          <Typography fontSize={13}>{color.hex}</Typography>
                        </Box>
                        <Button size="small" startIcon={<ContentCopy />} sx={{ ml: 'auto' }} onClick={() => handleCopy(color.hex, color.hex)}>
                          {copied === color.hex ? 'Kopyalandı!' : 'Kopyala'}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 1, minHeight: 320 }}>
                  <Typography fontWeight={700} gutterBottom>Palet Bilgileri</Typography>
                  <Typography sx={{ whiteSpace: 'pre-line', mb: 2 }}>{getPaletteInfo(paletteType, palette, baseColor)}</Typography>
                  <Typography fontWeight={700} gutterBottom>Kullanım Önerileri</Typography>
                  <Typography>{getUsageSuggestions(paletteType)}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* Alt: CSS/JSON kopyalama ve kaydetme */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" startIcon={<Code />} onClick={() => handleCopy(getPaletteCSS(), 'css')}>
            {copied === 'css' ? 'Kopyalandı!' : 'CSS/SCSS'}
          </Button>
          <Button variant="contained" color="success" startIcon={<DataObject />} onClick={() => handleCopy(getPaletteJSON(), 'json')}>
            {copied === 'json' ? 'Kopyalandı!' : 'JSON'}
          </Button>
          <Button variant="outlined" startIcon={<Save />} onClick={downloadJSON}>
            JSON İndir
          </Button>
          <Button variant="outlined" startIcon={<Save />} onClick={downloadASE}>
            GIMP Palette İndir
          </Button>
          <Button variant="contained" color="secondary" onClick={handleShare}>
            {copied === 'url' ? 'Kopyalandı!' : 'Paylaş'}
          </Button>
        </Box>

        {/* UI Preview */}
        <Paper sx={{ p: 3, mt: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Tasarım Önizleme</Typography>
          <Grid container spacing={3}>
            {/* Website Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Web Sitesi Önizleme</Typography>
              <Paper sx={{ p: 2, bgcolor: palette[0]?.hex || '#fff', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ height: 40, bgcolor: palette[1]?.hex || '#ccc', borderRadius: 1, mb: 2, display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography sx={{ color: palette[0]?.hex || '#fff', fontWeight: 700, fontSize: 14 }}>Logo</Typography>
                </Box>
                <Typography sx={{ color: palette[1]?.hex || '#333', fontWeight: 700, fontSize: 18, mb: 1 }}>Başlık Metni</Typography>
                <Typography sx={{ color: palette[2]?.hex || '#666', fontSize: 14, mb: 2 }}>Bu bir örnek paragraf metnidir.</Typography>
                <Button sx={{ bgcolor: palette[3]?.hex || palette[1]?.hex, color: palette[0]?.hex, '&:hover': { bgcolor: palette[3]?.hex } }}>
                  Buton
                </Button>
              </Paper>
            </Grid>

            {/* Swatch Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Renk Blokları</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {palette.map((color: PaletteColor, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: color.hex,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      pb: 0.5,
                      boxShadow: 2,
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontSize: 10, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {color.hex}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default ColorPalette; 